import { formatCurrency, formatNumber } from '../utils/format';
import './Dashboard.css';

interface DashboardProps {
  profile: any;
  statistics: any;
}

export function Dashboard({ profile, statistics }: DashboardProps) {
  if (!profile || !statistics) {
    return null;
  }

  const cards = [
    {
      title: 'Баланс',
      value: formatCurrency(profile.balance),
      subtitle: 'Доступно для вывода',
      icon: '💰',
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      title: 'Всего заработано',
      value: formatCurrency(profile.totalEarned),
      subtitle: `За ${getDaysSinceJoined(profile.joinedAt)} дней`,
      icon: '📈',
      gradient: 'from-blue-500 to-cyan-600',
    },
    {
      title: 'Рефералов',
      value: formatNumber(profile.referralCount),
      subtitle: `${profile.activeReferrals} активных`,
      icon: '👥',
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      title: 'Конверсия',
      value: `${statistics.overview.conversionRate.toFixed(2)}%`,
      subtitle: `${statistics.overview.totalRegistrations} регистраций`,
      icon: '🎯',
      gradient: 'from-orange-500 to-red-600',
    },
  ];

  return (
    <div className="dashboard">
      <div className="stats-grid">
        {cards.map((card, index) => (
          <div
            key={index}
            className="stat-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className={`stat-icon bg-gradient-to-br ${card.gradient}`}>
              <span>{card.icon}</span>
            </div>
            <div className="stat-content">
              <p className="stat-title">{card.title}</p>
              <h3 className="stat-value">{card.value}</h3>
              <p className="stat-subtitle">{card.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="quick-stats">
        <h2 className="section-title">Быстрая статистика</h2>
        <div className="quick-stats-grid">
          <div className="quick-stat">
            <span className="quick-stat-label">Кликов</span>
            <span className="quick-stat-value">{formatNumber(statistics.overview.totalClicks)}</span>
          </div>
          <div className="quick-stat">
            <span className="quick-stat-label">Депозитов</span>
            <span className="quick-stat-value">{formatNumber(statistics.overview.totalDeposits)}</span>
          </div>
          <div className="quick-stat">
            <span className="quick-stat-label">Уровень</span>
            <span className="quick-stat-value level-badge">{profile.level}</span>
          </div>
        </div>
      </div>

      {statistics.topReferrals && statistics.topReferrals.length > 0 && (
        <div className="top-referrals">
          <h2 className="section-title">Топ рефералы</h2>
          <div className="referral-list">
            {statistics.topReferrals.map((referral: any, index: number) => (
              <div key={referral.userId} className="referral-item">
                <div className="referral-rank">
                  {index === 0 && '🥇'}
                  {index === 1 && '🥈'}
                  {index === 2 && '🥉'}
                  {index > 2 && `#${index + 1}`}
                </div>
                <div className="referral-info">
                  <p className="referral-username">{referral.username}</p>
                  <p className="referral-date">
                    {formatDate(referral.registeredAt)}
                  </p>
                </div>
                <div className="referral-revenue">
                  {formatCurrency(referral.revenue)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getDaysSinceJoined(joinedAt: string): number {
  const joined = new Date(joinedAt);
  const now = new Date();
  return Math.floor((now.getTime() - joined.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return `${diffDays} дн. назад`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} нед. назад`;
  return `${Math.floor(diffDays / 30)} мес. назад`;
}
